<template>
    <div>
        <slide-up-modal
            v-if="showExpirationDateUpdateModal"
            ref="expirationDateUpdateModal"
            modal-id="expirationDateUpdateModal"
            modal-title="Mise à jour de la date d'expiration du document"
            @modal-closed="hideExpirationDateUpdateModal"
        >
            <p>
                Vous vous apprêtez à mettre à jour la date d'expiration du document <strong>{{ expectedDocumentName }}</strong>
            </p>

            <p style="font-weight: 600; margin-top: 20px; margin-bottom: 5px;">Date d'expiration du document</p>
            <input
                v-model="documentExpirationDate"
                type="date"
                class="form-control"
                :disabled="componentIsLoading"
                required
            >


            <div class="actions" style="margin-top: 20px;">
                <button class="btn btn-complete btn-sm" @click="performExpirationDateUpdate">Valider</button>
                <button class="btn btn-default btn-sm" @click="hideExpirationDateUpdateModal">Annuler</button>
            </div>
        </slide-up-modal>

        <div v-if="!componentIsLoading">
            <div v-if="customerDocument" class="existing-version">
                <p class="title">Version actuelle</p>
                
                <div class="subtitle">
                    <div class="text">
                        <p>Validé</p>
                        <p>Publié le {{ customerDocument.validated_at | moment-date-compact }}</p>
                    </div>
                </div>

                <div class="files-list">
                    <div v-for="(file, idx) in customerDocumentFiles" :key="'customer-document-file-' + idx" class="file-item">
                        <div>
                            <p class="index">Pièce n° {{ idx + 1 }}<span>, créé le {{ file.created_at | moment-date-compact }}</span></p>
    
                            <p class="name">{{ truncate(file.file_name, 50) }}</p>
                        </div>

                        <div class="buttons">
                            <a :href="getFileStreamUrl(file)" target="_blank" class="btn btn-complete btn-sm">
                                <i class="fa fa-eye"></i>
                                <span>Voir</span>
                            </a>
                        </div>
                    </div>
                </div>

                <div class="expiration-container" v-if="customerDocumentCanExpire">
                    <p>Expire le : <span>{{ customerDocument.expired_at | moment-date-compact }}</span></p>

                    <button class="btn btn-complete btn-xs" @click="displayExpirationDateUpdateModal()">
                        <span>Mettre à jour</span>
                    </button>
                </div>
            </div>
    
            <div v-else class="non-existing-version">
                <span style="font-weight: 600;">Aucune version du document n'a été validée</span>
            </div>
        </div>

        <component-loader v-else></component-loader>
    </div>
</template>

<script type="text/babel">
import api from 'store/api'
import SlideUpModal from 'components/Modals/SlideUpModal'
import ComponentLoader from 'components/ComponentLoader'
import customerDocumentFilesMixin from 'mixins/customerDocumentFiles'
import user from 'mixins/user'

export default {
    name:"DocumentPublishedVersionManager",
    
    props: {
        index: {
            type: Number,
            required: true
        },
        customerDocumentId: {
            type: Number,
            default: null
        },
        expectedDocumentName: {
            type: String,
            required: true
        }
    },

    mixins: [
        customerDocumentFilesMixin,
        user
    ],
    
    data () {
        return {
            componentIsLoading: false,
            customerDocument: null,
            showExpirationDateUpdateModal: false,
            documentExpirationDate: null
        }
    },

    components: {
        SlideUpModal,
        ComponentLoader
    },

    mounted() {
        this.fetchCustomerDocument()
    },

    computed: {
        customerDocumentFiles() {
            return this.customerDocument != null ? this.customerDocument.customer_document_files : []
        },
        customerDocumentCanExpire() {
            return this.customerDocument != null ? this.customerDocument.document_type.can_expire : false
        }
    },

    methods: {
        fetchCustomerDocument() {
            if (this.customerDocumentId) {
                this.componentIsLoading = true
    
                return api.getCustomerDocument(this.customerDocumentId, {with: 'document_type;customer_document_files'})
                    .then((response) => {
                        this.customerDocument = response.data.customer_document
                        this.resetDocumentExpirationDate()
                        this.componentIsLoading = false
                    })
            }
        },
        resetDocumentExpirationDate() {
            let expirationDate = null
            
            if (this.customerDocumentCanExpire) {
                expirationDate = moment(this.customerDocument.expired_at, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DD')
            }
            
            this.documentExpirationDate = expirationDate
        },
        displayExpirationDateUpdateModal() {
            this.showExpirationDateUpdateModal = true
        },
        hideExpirationDateUpdateModal() {
            this.resetDocumentExpirationDate()
            this.showExpirationDateUpdateModal = false
        },
        performExpirationDateUpdate() {
            if (this.showExpirationDateUpdateModal) {
                this.$refs.expirationDateUpdateModal.isLoading = true

                return api.updateCustomerDocumentExpirationDate(this.customerDocument.id, {expired_at: moment(this.documentExpirationDate, "YYYY-MM-DD").format("YYYY-MM-DD")})
                    .then(response => {
                        this.$emit('expiration-date-updated', this.index)
                    })
                    .catch(() => {
                        this.hideExpirationDateUpdateModal()
                        this.addNotification("Une erreur s'est produite, merci de réessauer", 'error')
                    })
            }
        }
    },

    watch: {
        customerDocumentId() {
            this.fetchCustomerDocument()
        }
    }
}
</script>

<style lang="scss" scoped>
    .existing-version {
        p {
            margin: 0;
            font-size: inherit;
        }
        .title {
            text-transform: uppercase;
            font-weight: 600;
            font-size: 16px;
        }
        .subtitle {
            margin-top: 10px;
            display: flex;
            justify-content: space-between;
            align-items: center;

            .text {
                font-size: 26px;

                p + p {
                    font-size: 13px;
                    margin-top: 5px;
                }
            }
        }

        .files-list {
            margin-top: 10px;

            .file-item {
                display: flex;
                justify-content: space-between;
                align-items: center;
                background-color: #fff;
                padding: 6px 8px;
                margin-bottom: 6px;
                border-radius: 3px;

                .index {
                    font-weight: 600;
                    font-size: 16px;
                    color: #555;

                    span {
                        font-size: 14px;
                        color: #888;
                    }
                }
                .name {
                    font-size: 14px;
                    color: #777;
                }
            }
        }

        .expiration-container {
            margin-top: 20px;
            display: flex;
            align-items: center;
            gap: 10px;

            p {
                font-size: 14px;
                font-weight: 600;
            }
        }

        button + button {
            margin-left: 5px;
        }
    }

    .non-existing-version {
        min-height: 200px;
        display: flex;
        justify-content: center;
        align-items: center;
    }
</style>


// WEBPACK FOOTER //
// client/components/CustomerDocuments/PublishedVersionManager.vue